import { useState } from "react";
import useSWR from "swr";
import { http, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Copy, Plus, Trash2, Pencil, Clock, Repeat, Calendar as CalIcon, BellRing, CheckCircle2, XCircle } from "lucide-react";

const fetcher = (url) => http.get(url).then((r) => r.data);

const WEEKDAYS = [
  { v: 0, s: "Sen" },
  { v: 1, s: "Sel" },
  { v: 2, s: "Rab" },
  { v: 3, s: "Kam" },
  { v: 4, s: "Jum" },
  { v: 5, s: "Sab" },
  { v: 6, s: "Min" },
];

const emptyForm = {
  id: null,
  title: "",
  description: "",
  time: "09:00",
  recurrence: "once",
  date: new Date().toISOString().slice(0, 10),
  weekdays: [],
  reminder_minutes_before: 10,
  is_active: true,
};

function describeRecurrence(sc) {
  if (sc.recurrence === "once") return sc.date || "-";
  if (sc.recurrence === "daily") return "Setiap hari";
  if (sc.recurrence === "weekly") {
    const days = (sc.weekdays || []).map((d) => WEEKDAYS.find((w) => w.v === d)?.s).filter(Boolean).join(", ");
    return days ? `Mingguan: ${days}` : "Mingguan";
  }
  return "-";
}

export default function Jadwal() {
  const { data: schedules, mutate } = useSWR("/schedules", fetcher);
  const [form, setForm] = useState({ open: false, ...emptyForm });

  const openNew = () => setForm({ open: true, ...emptyForm });
  const openEdit = (sc) =>
    setForm({
      open: true,
      id: sc.id,
      title: sc.title,
      description: sc.description || "",
      time: sc.time,
      recurrence: sc.recurrence,
      date: sc.date || new Date().toISOString().slice(0, 10),
      weekdays: sc.weekdays || [],
      reminder_minutes_before: sc.reminder_minutes_before,
      is_active: sc.is_active,
    });

  const toggleWeekday = (v) => {
    const cur = form.weekdays.includes(v) ? form.weekdays.filter((x) => x !== v) : [...form.weekdays, v];
    setForm({ ...form, weekdays: cur });
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Judul wajib diisi");
    if (form.recurrence === "weekly" && form.weekdays.length === 0)
      return toast.error("Pilih setidaknya satu hari untuk jadwal mingguan");
    const payload = {
      title: form.title.trim(),
      description: form.description,
      time: form.time,
      recurrence: form.recurrence,
      date: form.recurrence === "once" ? form.date : null,
      weekdays: form.recurrence === "weekly" ? form.weekdays : [],
      reminder_minutes_before: Number(form.reminder_minutes_before) || 0,
      is_active: form.is_active,
    };
    try {
      if (form.id) await http.patch(`/schedules/${form.id}`, payload);
      else await http.post("/schedules", payload);
      toast.success("Jadwal tersimpan");
      setForm({ open: false, ...emptyForm });
      mutate();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus jadwal ini?")) return;
    try {
      await http.delete(`/schedules/${id}`);
      toast.success("Jadwal dihapus");
      mutate();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const toggleActive = async (sc) => {
    try {
      await http.patch(`/schedules/${sc.id}`, { ...sc, is_active: !sc.is_active });
      mutate();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const duplicate = async (sc) => {
    try {
      const payload = {
        title: `${sc.title} (salinan)`,
        description: sc.description || "",
        time: sc.time,
        recurrence: sc.recurrence,
        date: sc.recurrence === "once" ? sc.date : null,
        weekdays: sc.recurrence === "weekly" ? (sc.weekdays || []) : [],
        reminder_minutes_before: sc.reminder_minutes_before,
        is_active: sc.is_active,
      };
      await http.post("/schedules", payload);
      toast.success(`Jadwal disalin: "${payload.title}"`);
      mutate();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const items = schedules || [];

  return (
    <div className="space-y-8">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Produktivitas</div>
          <h1 className="mt-1 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
            Jadwal & Pengingat WhatsApp
          </h1>
          <p className="mt-1 text-slate-600">Kelola kegiatan harian Anda. Ringkasan pagi + pengingat per-kegiatan dikirim otomatis ke WhatsApp.</p>
        </div>
        <Dialog open={form.open} onOpenChange={(o) => setForm({ ...form, open: o })}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700 gap-2" onClick={openNew} data-testid="add-schedule-button">
              <Plus className="w-4 h-4" /> Tambah Jadwal
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg" data-testid="schedule-modal">
            <DialogHeader>
              <DialogTitle>{form.id ? "Edit Jadwal" : "Tambah Jadwal"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Judul kegiatan</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Mis. Meeting tim marketing"
                  data-testid="schedule-title-input"
                />
              </div>
              <div>
                <Label>Deskripsi (opsional)</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder={"Contoh:\n- Siapkan slide\n- Undang tim\n- Cek Zoom link"}
                  rows={4}
                  className="resize-y min-h-[96px] font-[inherit] whitespace-pre-wrap"
                  data-testid="schedule-desc-input"
                />
                <p className="mt-1 text-xs text-slate-500">Tekan Enter untuk baris baru. Isi ini akan dikirim apa adanya ke WhatsApp.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Jam (24 jam)</Label>
                  <Input
                    type="time"
                    value={form.time}
                    onChange={(e) => setForm({ ...form, time: e.target.value })}
                    data-testid="schedule-time-input"
                  />
                </div>
                <div>
                  <Label>Ingatkan (menit sebelum)</Label>
                  <Input
                    type="number"
                    min={0}
                    value={form.reminder_minutes_before}
                    onChange={(e) => setForm({ ...form, reminder_minutes_before: e.target.value })}
                    data-testid="schedule-remind-input"
                  />
                </div>
              </div>
              <div>
                <Label>Tipe pengulangan</Label>
                <Select value={form.recurrence} onValueChange={(v) => setForm({ ...form, recurrence: v })}>
                  <SelectTrigger data-testid="schedule-recurrence-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="once">Sekali</SelectItem>
                    <SelectItem value="daily">Setiap hari</SelectItem>
                    <SelectItem value="weekly">Mingguan (pilih hari)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.recurrence === "once" && (
                <div>
                  <Label>Tanggal</Label>
                  <Input
                    type="date"
                    value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                    data-testid="schedule-date-input"
                  />
                </div>
              )}
              {form.recurrence === "weekly" && (
                <div>
                  <Label>Hari</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {WEEKDAYS.map((w) => {
                      const on = form.weekdays.includes(w.v);
                      return (
                        <button
                          key={w.v}
                          type="button"
                          onClick={() => toggleWeekday(w.v)}
                          data-testid={`weekday-${w.v}`}
                          className={`h-9 w-11 rounded-md text-sm font-medium border transition-colors ${
                            on
                              ? "bg-blue-600 border-blue-600 text-white"
                              : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                          }`}
                        >
                          {w.s}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
                <div className="text-sm text-slate-700">Aktifkan pengingat WA</div>
                <Switch
                  checked={form.is_active}
                  onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                  data-testid="schedule-active-switch"
                />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={save} className="bg-blue-600 hover:bg-blue-700" data-testid="schedule-save-button">
                Simpan
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </header>

      <Card className="overflow-hidden">
        <div className="grid grid-cols-12 px-6 py-3 border-b border-slate-100 text-xs uppercase tracking-[0.1em] font-semibold text-slate-500 bg-slate-50/60">
          <div className="col-span-1 text-center">Aktif</div>
          <div className="col-span-4">Kegiatan</div>
          <div className="col-span-2">Jam</div>
          <div className="col-span-3">Pengulangan</div>
          <div className="col-span-2 text-right">Aksi</div>
        </div>
        <div className="divide-y divide-slate-100">
          {items.length === 0 && (
            <div className="px-6 py-16 text-center text-slate-500">
              Belum ada jadwal. Tekan <span className="font-medium text-slate-700">Tambah Jadwal</span> untuk mulai.
            </div>
          )}
          {items.map((sc) => (
            <div key={sc.id} className="grid grid-cols-12 px-6 py-3 items-center" data-testid={`schedule-row-${sc.id}`}>
              <div className="col-span-1 flex justify-center">
                <button
                  onClick={() => toggleActive(sc)}
                  className={sc.is_active ? "text-emerald-500" : "text-slate-300"}
                  data-testid={`schedule-toggle-${sc.id}`}
                  title={sc.is_active ? "Aktif" : "Nonaktif"}
                >
                  {sc.is_active ? <CheckCircle2 className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
                </button>
              </div>
              <div className="col-span-4">
                <div className="text-sm font-medium text-slate-900">{sc.title}</div>
                {sc.description && <div className="text-xs text-slate-500 whitespace-pre-wrap">{sc.description}</div>}
                <div className="text-[11px] text-slate-400 mt-0.5 inline-flex items-center gap-1">
                  <BellRing className="w-3 h-3" /> Ingatkan {sc.reminder_minutes_before} menit sebelum
                </div>
              </div>
              <div className="col-span-2 text-sm text-slate-700 font-medium tabular inline-flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-slate-400" /> {sc.time}
              </div>
              <div className="col-span-3 text-sm text-slate-700 inline-flex items-center gap-1.5">
                {sc.recurrence === "once" ? <CalIcon className="w-3.5 h-3.5 text-slate-400" /> : <Repeat className="w-3.5 h-3.5 text-slate-400" />}
                {describeRecurrence(sc)}
              </div>
              <div className="col-span-2 flex justify-end gap-1">
                <Button variant="ghost" size="icon" onClick={() => duplicate(sc)} data-testid={`copy-schedule-${sc.id}`} className="text-slate-500 hover:text-blue-600" title="Salin jadwal">
                  <Copy className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => openEdit(sc)} data-testid={`edit-schedule-${sc.id}`} className="text-slate-500 hover:text-slate-900" title="Edit">
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => del(sc.id)} data-testid={`delete-schedule-${sc.id}`} className="text-slate-500 hover:text-rose-600" title="Hapus">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
